import { productStore } from './productStore';
import { agreementFor } from '../../clientReporting/product/sources/incidents';
import { inVersion } from '../productMock';

/** Targets are counted in service hours, not wall-clock ones. */
export const PRIORITIES = ['p1', 'p2', 'p3', 'p4'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const SLA_TARGETS = ['response', 'resolution'] as const;
export type SlaTarget = (typeof SLA_TARGETS)[number];

/** The bands are the standard contract's, so only the multiplier is a product's own to set. */
export const OUT_OF_HOURS_BANDS = ['evening', 'night'] as const;
export type OutOfHoursBand = (typeof OUT_OF_HOURS_BANDS)[number];

/** In the order they are bought; only P1's response differs between them. */
export const SUPPORT_AGREEMENTS = ['standard', 'bronze', 'silver', 'gold'] as const;
export type SupportAgreement = (typeof SUPPORT_AGREEMENTS)[number];

/** Support sold for one fixed price: no window, no multipliers and no targets to hold anyone to. */
export const FLAT_FEE = 'flatFee';
export interface FlatFee {
  agreement: typeof FLAT_FEE;
}

/** What the picker offers, the flat fee after the four agreements. */
export const AGREEMENT_OPTIONS = [...SUPPORT_AGREEMENTS, FLAT_FEE] as const;

export interface AgreementTerms {
  from: string;
  to: string;
  days: number;
  /** Gold answers P1 and P2 around the clock, whatever the window says for the rest. */
  alwaysOnTopPriorities: boolean;
  targets: Record<Priority, Record<SlaTarget, number>>;
}

const BELOW_P1: Record<'p2' | 'p3' | 'p4', Record<SlaTarget, number>> = {
  p2: { response: 2, resolution: 24 },
  p3: { response: 4, resolution: 40 },
  p4: { response: 8, resolution: 120 },
};

export const AGREEMENT_TERMS: Record<SupportAgreement, AgreementTerms> = {
  standard: {
    from: '09:00',
    to: '17:00',
    days: 5,
    alwaysOnTopPriorities: false,
    targets: { p1: { response: 2, resolution: 8 }, ...BELOW_P1 },
  },
  bronze: {
    from: '08:00',
    to: '20:00',
    days: 5,
    alwaysOnTopPriorities: false,
    targets: { p1: { response: 1, resolution: 8 }, ...BELOW_P1 },
  },
  silver: {
    from: '08:00',
    to: '20:00',
    days: 7,
    alwaysOnTopPriorities: false,
    targets: { p1: { response: 0.5, resolution: 8 }, ...BELOW_P1 },
  },
  gold: {
    from: '08:00',
    to: '20:00',
    days: 7,
    alwaysOnTopPriorities: true,
    targets: { p1: { response: 0.5, resolution: 8 }, ...BELOW_P1 },
  },
};

/** The same out-of-hours multipliers whichever agreement was signed. */
const DEFAULT_RATES: Record<OutOfHoursBand, number> = { evening: 1.25, night: 1.5 };

/** Read off the rows the client's own SLA table is drawn from, so the two cannot quote different prices. */
const priorityRatesFor = (agreement: SupportAgreement): Record<Priority, number> =>
  Object.fromEntries(
    agreementFor(agreement).targets.map(target => [target.priority.toLowerCase(), target.rateMultiplier]),
  ) as Record<Priority, number>;

export interface ServiceHours {
  /** The window is read off its terms rather than stored here: it is not a product's to negotiate. */
  agreement: SupportAgreement;
  /** Hours allowed per priority, per target. */
  targets: Record<Priority, Record<SlaTarget, number>>;
  /** What an hour costs in each out-of-hours band, as a multiple of the standard rate. */
  rates: Record<OutOfHoursBand, number>;
  /** What an hour costs at each priority. Compounds with the band: a P1 at night is both. */
  priorityRates: Record<Priority, number>;
}

/** An agreement's own terms, as a whole card: what picking it fills in. */
export const serviceHoursFor = (agreement: SupportAgreement): ServiceHours => {
  const terms = AGREEMENT_TERMS[agreement];
  return {
    agreement,
    targets: {
      p1: { ...terms.targets.p1 },
      p2: { ...terms.targets.p2 },
      p3: { ...terms.targets.p3 },
      p4: { ...terms.targets.p4 },
    },
    rates: { ...DEFAULT_RATES },
    priorityRates: priorityRatesFor(agreement),
  };
};

/** Whatever was sold on a product: an agreement's terms, or a flat fee with none. */
export type SoldSupport = ServiceHours | FlatFee;

export const serviceHoursStorageKey = (productId: string, version?: string): string =>
  inVersion(`productServiceHours:${productId}`, version);

// A value that does not parse reads as nothing sold.
const readRaw = (productId: string, version?: string): unknown => {
  try {
    // A version nobody has saved reads the agreement from before versions, not the standard one.
    const raw =
      productStore.getItem(serviceHoursStorageKey(productId, version)) ??
      productStore.getItem(serviceHoursStorageKey(productId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Hours to the half. Anything that is not a positive, finite number is not a target. */
export const asHours = (value: unknown): number | null => {
  const hours = typeof value === 'string' ? Number(value.trim().replace(',', '.')) : value;
  if (typeof hours !== 'number' || !Number.isFinite(hours)) return null;
  // Rounded before the test, or 0.004 passes as a target and is stored as the 0 the reader rejects.
  const rounded = Math.round(hours * 100) / 100;
  return rounded > 0 ? rounded : null;
};

/** Out of hours is never cheaper than in them, so anything under 1 is not a multiplier. */
export const asRate = (value: unknown): number | null => {
  const rate = typeof value === 'string' ? Number(value.trim().replace(',', '.')) : value;
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate < 1) return null;
  return Math.round(rate * 100) / 100;
};

/** Null where nobody has picked an agreement, which is not the same as the standard one. */
export const readServiceHours = (productId: string, version?: string): SoldSupport | null => {
  const raw = readRaw(productId, version);
  if (!raw || typeof raw !== 'object') return null;
  if ((raw as { agreement?: unknown }).agreement === FLAT_FEE) return { agreement: FLAT_FEE };
  const stored = raw as Partial<ServiceHours>;
  // Falls back to the agreement's own terms, so a half-written card is not read as the standard one.
  const agreement = SUPPORT_AGREEMENTS.includes(stored.agreement as SupportAgreement)
    ? (stored.agreement as SupportAgreement)
    : 'standard';
  const signed = serviceHoursFor(agreement);
  const targets = {} as ServiceHours['targets'];
  PRIORITIES.forEach(priority => {
    const row = (stored.targets ?? {})[priority] as Record<string, unknown> | undefined;
    targets[priority] = { ...signed.targets[priority] };
    SLA_TARGETS.forEach(target => {
      const hours = asHours(row?.[target]);
      if (hours !== null) targets[priority][target] = hours;
    });
  });
  const rates = { ...signed.rates };
  OUT_OF_HOURS_BANDS.forEach(band => {
    const rate = asRate((stored.rates ?? {})[band]);
    if (rate !== null) rates[band] = rate;
  });
  const priorityRates = { ...signed.priorityRates };
  PRIORITIES.forEach(priority => {
    const rate = asRate((stored.priorityRates ?? {})[priority]);
    if (rate !== null) priorityRates[priority] = rate;
  });
  return { agreement, targets, rates, priorityRates };
};

export const writeServiceHours = (productId: string, hours: SoldSupport, version?: string): void => {
  productStore.setItem(serviceHoursStorageKey(productId, version), JSON.stringify(hours));
};

/** Whole hours stay whole, halves keep their half: 0.5, 2, 120. */
export const formatHours = (hours: number): string => String(hours);

/** Both places, always: ×1.25 beside ×1.50 rather than beside ×1.5. */
export const formatRate = (rate: number): string => rate.toFixed(2);

/** The card as typed: every field a string, so a half-typed number survives a re-render. */
export interface ServiceHoursDraft {
  agreement: SupportAgreement;
  targets: Record<Priority, Record<SlaTarget, string>>;
  rates: Record<OutOfHoursBand, string>;
  priorityRates: Record<Priority, string>;
}

export const draftFromServiceHours = (hours: ServiceHours): ServiceHoursDraft => ({
  agreement: hours.agreement,
  targets: Object.fromEntries(
    PRIORITIES.map(priority => [
      priority,
      Object.fromEntries(SLA_TARGETS.map(target => [target, formatHours(hours.targets[priority][target])])),
    ]),
  ) as ServiceHoursDraft['targets'],
  rates: Object.fromEntries(
    OUT_OF_HOURS_BANDS.map(band => [band, formatRate(hours.rates[band])]),
  ) as ServiceHoursDraft['rates'],
  priorityRates: Object.fromEntries(
    PRIORITIES.map(priority => [priority, formatRate(hours.priorityRates[priority])]),
  ) as ServiceHoursDraft['priorityRates'],
});

/** The card as an agreement fills it in, keeping the out-of-hours bands typed: those are not its terms. */
export const draftFromAgreement = (
  agreement: SupportAgreement,
  rates: ServiceHoursDraft['rates'],
): ServiceHoursDraft => ({
  ...draftFromServiceHours(serviceHoursFor(agreement)),
  rates,
});

/** Only called once the draft is whole, so a blank field cannot reach storage as a zero. */
export const serviceHoursFromDraft = (draft: ServiceHoursDraft): ServiceHours => ({
  agreement: draft.agreement,
  targets: Object.fromEntries(
    PRIORITIES.map(priority => [
      priority,
      Object.fromEntries(
        SLA_TARGETS.map(target => [
          target,
          asHours(draft.targets[priority][target]) ?? AGREEMENT_TERMS[draft.agreement].targets[priority][target],
        ]),
      ),
    ]),
  ) as ServiceHours['targets'],
  rates: Object.fromEntries(
    OUT_OF_HOURS_BANDS.map(band => [band, asRate(draft.rates[band]) ?? DEFAULT_RATES[band]]),
  ) as ServiceHours['rates'],
  priorityRates: Object.fromEntries(
    PRIORITIES.map(priority => [
      priority,
      asRate(draft.priorityRates[priority]) ?? priorityRatesFor(draft.agreement)[priority],
    ]),
  ) as ServiceHours['priorityRates'],
});

/** Keyed `priority:target`, `rate:band` or `priorityRate:priority`; never the window, nobody types it. */
export const invalidServiceFields = (draft: ServiceHoursDraft): string[] => {
  const fields: string[] = [];
  PRIORITIES.forEach(priority =>
    SLA_TARGETS.forEach(target => {
      if (asHours(draft.targets[priority][target]) === null) fields.push(`${priority}:${target}`);
    }),
  );
  OUT_OF_HOURS_BANDS.forEach(band => {
    if (asRate(draft.rates[band]) === null) fields.push(`rate:${band}`);
  });
  PRIORITIES.forEach(priority => {
    if (asRate(draft.priorityRates[priority]) === null) fields.push(`priorityRate:${priority}`);
  });
  return Array.from(new Set(fields));
};
